import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Search, RefreshCw, Flag, Trash2, AlertTriangle, CheckCircle, X } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface DuplicatePair {
    senta_id_a: string;
    title_a: string | null;
    ref_a: string | null;
    state_a: string | null;
    created_a: string | null;
    address_a: string | null;
    company_number_a: string | null;
    senta_id_b: string;
    title_b: string | null;
    ref_b: string | null;
    state_b: string | null;
    created_b: string | null;
    address_b: string | null;
    company_number_b: string | null;
    title_similarity: number;
    shared_email_count: number;
    shared_emails: string[];
    company_match: boolean;
    ref_match: boolean;
    address_match: boolean;
    confidence: number;
    flagged_a: boolean;
    flagged_b: boolean;
}

interface DeletionFlag {
    Id: number;
    SentaId: string;
    Title: string | null;
    ClientRef: string | null;
    Reason: string | null;
    DuplicateOfSentaId: string | null;
    FlaggedBy: string;
    FlaggedAt: string;
    Status: string;
}

const CLIENT_STATE_LABEL: Record<string, string> = {
    "l-clientstate-c": "Client",
    "l-clientstate-p": "Prospect",
    "l-clientstate-a": "Archived",
};

const confidenceColor = (c: number) => {
    if (c >= 0.8) return "#f85149";
    if (c >= 0.6) return "#d29922";
    if (c >= 0.4) return "#2ea043";
    return "#8b949e";
};

const ClientDuplicates: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const apiBaseUrl = getApiBaseUrl();

    const [pairs, setPairs] = useState<DuplicatePair[]>([]);
    const [cacheInfo, setCacheInfo] = useState<{ computedAt: string; status: string } | null>(null);
    const [flags, setFlags] = useState<DeletionFlag[]>([]);
    const [filter, setFilter] = useState("");
    const [minSimilarity, setMinSimilarity] = useState(0.6);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [onlyHighConfidence, setOnlyHighConfidence] = useState(false);
    const [hideFlaggedPairs, setHideFlaggedPairs] = useState(true);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
    const [view, setView] = useState<"pairs" | "flags">("pairs");

    const getToken = useCallback(async () => {
        if (!accounts[0]) throw new Error("Not signed in");
        return await acquireTokenSafe(instance, accounts);
    }, [instance, accounts]);

    const fetchDuplicates = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        setBusy(true);
        setMessage(null);
        try {
            const token = await getToken();
            const refreshParam = forceRefresh ? '&refresh=true' : '';
            const url = `${apiBaseUrl}/api/client-duplicates?minSimilarity=${minSimilarity}&includeArchived=${includeArchived}${refreshParam}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setPairs(data.pairs || []);
            setFlags(data.flags || []);
            if (data.cache?.computed_at) {
                setCacheInfo({ computedAt: data.cache.computed_at, status: data.cache.status });
            }
        } catch (e: any) {
            setMessage({ kind: "err", text: e.message || "Failed to load duplicates" });
        } finally {
            setLoading(false);
            setBusy(false);
        }
    }, [apiBaseUrl, getToken, minSimilarity, includeArchived, setLoading]);

    const fetchFlags = useCallback(async () => {
        try {
            const token = await getToken();
            const res = await fetch(`${apiBaseUrl}/api/client-duplicates/flags`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setFlags(data.flags || []);
        } catch (e: any) {
            setMessage({ kind: "err", text: e.message || "Failed to load flags" });
        }
    }, [apiBaseUrl, getToken]);

    useEffect(() => {
        fetchDuplicates();
        // eslint-disable-next-line
    }, []);

    const flagClient = async (
        sentaId: string,
        title: string | null,
        clientRef: string | null,
        duplicateOfSentaId: string | null,
        reason: string
    ) => {
        try {
            const token = await getToken();
            const res = await fetch(`${apiBaseUrl}/api/client-duplicates/flag`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ sentaId, title, clientRef, duplicateOfSentaId, reason }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${res.status}`);
            }
            setMessage({ kind: "ok", text: `Flagged ${title || sentaId} for deletion` });
            // Optimistic update
            setPairs(prev => prev.map(p => ({
                ...p,
                flagged_a: p.senta_id_a === sentaId ? true : p.flagged_a,
                flagged_b: p.senta_id_b === sentaId ? true : p.flagged_b,
            })));
            fetchFlags();
        } catch (e: any) {
            setMessage({ kind: "err", text: e.message || "Failed to flag client" });
        }
    };

    const unflagClient = async (sentaId: string, title?: string | null) => {
        try {
            const token = await getToken();
            const res = await fetch(`${apiBaseUrl}/api/client-duplicates/unflag`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ sentaId, status: "dismissed" }),
            });
            if (!res.ok) throw new Error(await res.text());
            setMessage({ kind: "ok", text: `Removed flag for ${title || sentaId}` });
            setPairs(prev => prev.map(p => ({
                ...p,
                flagged_a: p.senta_id_a === sentaId ? false : p.flagged_a,
                flagged_b: p.senta_id_b === sentaId ? false : p.flagged_b,
            })));
            setFlags(prev => prev.filter(f => f.SentaId !== sentaId));
        } catch (e: any) {
            setMessage({ kind: "err", text: e.message || "Failed to unflag client" });
        }
    };

    const filteredPairs = useMemo(() => {
        let out = pairs;
        if (onlyHighConfidence) out = out.filter(p => p.confidence >= 0.7);
        if (hideFlaggedPairs) out = out.filter(p => !p.flagged_a && !p.flagged_b);
        const f = filter.trim().toLowerCase();
        if (f) {
            out = out.filter(p =>
                (p.title_a || "").toLowerCase().includes(f) ||
                (p.title_b || "").toLowerCase().includes(f) ||
                (p.ref_a || "").toLowerCase().includes(f) ||
                (p.ref_b || "").toLowerCase().includes(f) ||
                p.senta_id_a.toLowerCase().includes(f) ||
                p.senta_id_b.toLowerCase().includes(f) ||
                (p.shared_emails || []).some(e => e.toLowerCase().includes(f))
            );
        }
        return out;
    }, [pairs, filter, onlyHighConfidence, hideFlaggedPairs]);

    const renderClientCell = (
        side: "a" | "b",
        row: DuplicatePair
    ) => {
        const sentaId = side === "a" ? row.senta_id_a : row.senta_id_b;
        const title = side === "a" ? row.title_a : row.title_b;
        const ref = side === "a" ? row.ref_a : row.ref_b;
        const state = side === "a" ? row.state_a : row.state_b;
        const created = side === "a" ? row.created_a : row.created_b;
        const company = side === "a" ? row.company_number_a : row.company_number_b;
        const flagged = side === "a" ? row.flagged_a : row.flagged_b;
        const otherSentaId = side === "a" ? row.senta_id_b : row.senta_id_a;
        return (
            <div style={{ padding: "8px 0", lineHeight: 1.4, opacity: flagged ? 0.55 : 1 }}>
                <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                    {title || <em style={{ color: "var(--text-secondary)" }}>(no title)</em>}
                    {flagged && (
                        <span style={{
                            marginLeft: 8, fontSize: 11, padding: "2px 6px", borderRadius: 4,
                            background: "#f8514922", color: "#f85149", fontWeight: 600,
                        }}>FLAGGED</span>
                    )}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    {ref && <span>ref: <code>{ref}</code></span>}
                    {ref && state && <span> · </span>}
                    {state && <span>{CLIENT_STATE_LABEL[state] || state}</span>}
                    {company && <span> · CRO: {company}</span>}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary, var(--text-secondary))" }}>
                    senta: <code>{sentaId}</code>
                    {created && <span> · created {new Date(created).toLocaleDateString()}</span>}
                </div>
                <div style={{ marginTop: 6 }}>
                    {flagged ? (
                        <button
                            onClick={() => unflagClient(sentaId, title)}
                            style={btnStyle("secondary")}
                            title="Remove deletion flag"
                        >
                            <X size={12} style={{ marginRight: 4 }} /> Unflag
                        </button>
                    ) : (
                        <button
                            onClick={() => flagClient(sentaId, title, ref, otherSentaId, `Duplicate of ${otherSentaId}`)}
                            style={btnStyle("danger")}
                            title="Flag this client for deletion"
                        >
                            <Flag size={12} style={{ marginRight: 4 }} /> Flag for deletion
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const columns: TableColumn<DuplicatePair>[] = [
        {
            name: "Confidence",
            width: "115px",
            sortable: true,
            selector: (row) => row.confidence,
            cell: (row) => (
                <div style={{ textAlign: "center", width: "100%" }}>
                    <div style={{
                        fontWeight: 700, fontSize: 16,
                        color: confidenceColor(row.confidence),
                    }}>
                        {Math.round(row.confidence * 100)}%
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-secondary)", marginTop: 2 }}>
                        title {Math.round((row.title_similarity || 0) * 100)}%
                    </div>
                    {row.shared_email_count > 0 && (
                        <div style={{ fontSize: 10, color: "#2ea043", marginTop: 2 }}>
                            {row.shared_email_count} shared email{row.shared_email_count !== 1 ? "s" : ""}
                        </div>
                    )}
                    {row.company_match && (
                        <div style={{ fontSize: 10, color: "#2ea043" }}>CRO match</div>
                    )}
                    {row.ref_match && (
                        <div style={{ fontSize: 10, color: "#2ea043" }}>ref match</div>
                    )}
                    {row.address_match && (
                        <div style={{ fontSize: 10, color: "#2ea043" }}>addr match</div>
                    )}
                </div>
            ),
        },
        {
            name: "Client A",
            grow: 2,
            cell: (row) => renderClientCell("a", row),
        },
        {
            name: "Client B",
            grow: 2,
            cell: (row) => renderClientCell("b", row),
        },
        {
            name: "Shared emails",
            grow: 1,
            cell: (row) => row.shared_emails && row.shared_emails.length ? (
                <div style={{ fontSize: 11, padding: "4px 0" }}>
                    {row.shared_emails.slice(0, 3).map((e, i) => (
                        <div key={i} style={{ color: "var(--text-secondary)" }}>{e}</div>
                    ))}
                    {row.shared_emails.length > 3 && (
                        <div style={{ color: "var(--text-tertiary, var(--text-secondary))" }}>
                            +{row.shared_emails.length - 3} more
                        </div>
                    )}
                </div>
            ) : <span style={{ color: "var(--text-tertiary, var(--text-secondary))" }}>—</span>,
        },
    ];

    const flagColumns: TableColumn<DeletionFlag>[] = [
        {
            name: "Client",
            grow: 2,
            cell: (row) => (
                <div style={{ padding: "8px 0" }}>
                    <div style={{ fontWeight: 600 }}>{row.Title || row.SentaId}</div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        {row.ClientRef && <span>ref: <code>{row.ClientRef}</code> · </span>}
                        senta: <code>{row.SentaId}</code>
                    </div>
                </div>
            ),
        },
        { name: "Reason", grow: 2, selector: (row) => row.Reason || "—", wrap: true },
        { name: "Flagged by", selector: (row) => row.FlaggedBy, wrap: true },
        {
            name: "When",
            selector: (row) => row.FlaggedAt,
            format: (row) => new Date(row.FlaggedAt).toLocaleString(),
        },
        {
            name: "Action",
            width: "120px",
            cell: (row) => (
                <button onClick={() => unflagClient(row.SentaId, row.Title)} style={btnStyle("secondary")}>
                    <X size={12} style={{ marginRight: 4 }} /> Unflag
                </button>
            ),
        },
    ];

    return (
        <div style={{ padding: 24, color: "var(--text-primary)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                    <h1 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
                        <Trash2 size={24} /> Client Duplicates
                    </h1>
                    <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>
                        Probable duplicate clients based on title similarity and shared contact emails. Flag duplicates for deletion.
                    </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        onClick={() => setView("pairs")}
                        style={tabStyle(view === "pairs")}
                    >Duplicates</button>
                    <button
                        onClick={() => { setView("flags"); fetchFlags(); }}
                        style={tabStyle(view === "flags")}
                    >Flagged ({flags.length})</button>
                </div>
            </div>

            {message && (
                <div style={{
                    padding: "10px 14px", marginBottom: 12, borderRadius: 6,
                    background: message.kind === "ok" ? "#2ea04322" : "#f8514922",
                    color: message.kind === "ok" ? "#2ea043" : "#f85149",
                    display: "flex", alignItems: "center", gap: 8, fontSize: 13,
                }}>
                    {message.kind === "ok" ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                    {message.text}
                </div>
            )}

            {view === "pairs" && (
                <>
                    <div style={{
                        display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
                        background: "var(--bg-secondary)", padding: 12, borderRadius: 8, marginBottom: 12,
                    }}>
                        <div style={{ position: "relative", flex: "1 1 240px", minWidth: 200 }}>
                            <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "var(--text-secondary)" }} />
                            <input
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                                placeholder="Search by name, ref, senta_id, email…"
                                style={{
                                    width: "100%", padding: "8px 10px 8px 30px", borderRadius: 6,
                                    border: "1px solid var(--border-color, #30363d)",
                                    background: "var(--bg-primary)", color: "var(--text-primary)",
                                }}
                            />
                        </div>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                            Min title similarity:
                            <input
                                type="range" min={0.3} max={0.95} step={0.05}
                                value={minSimilarity}
                                onChange={e => setMinSimilarity(parseFloat(e.target.value))}
                            />
                            <span style={{ fontVariantNumeric: "tabular-nums", width: 36 }}>
                                {Math.round(minSimilarity * 100)}%
                            </span>
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                            <input type="checkbox" checked={onlyHighConfidence} onChange={e => setOnlyHighConfidence(e.target.checked)} />
                            High confidence only (≥70%)
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                            <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} />
                            Include archived
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                            <input type="checkbox" checked={hideFlaggedPairs} onChange={e => setHideFlaggedPairs(e.target.checked)} />
                            Hide pairs already flagged
                        </label>
                        <button onClick={() => fetchDuplicates()} disabled={busy} style={btnStyle("primary")}>
                            <RefreshCw size={14} style={{ marginRight: 6 }} />
                            {busy ? "Loading…" : "Refresh"}
                        </button>
                    </div>

                    <div style={{
                        fontSize: 12, color: "var(--text-secondary)", marginBottom: 8,
                        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                    }}>
                        <span>
                            Showing {filteredPairs.length} of {pairs.length} pair{pairs.length !== 1 ? "s" : ""}
                        </span>
                        {cacheInfo && (
                            <span title={`Cache: ${cacheInfo.status}`}>
                                · Last computed {new Date(cacheInfo.computedAt).toLocaleString("en-IE", {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                })}
                                {" "}
                                <button
                                    onClick={() => fetchDuplicates(true)}
                                    disabled={busy}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "var(--accent-primary)",
                                        cursor: busy ? "default" : "pointer",
                                        padding: 0,
                                        fontSize: 12,
                                        textDecoration: "underline",
                                    }}
                                >
                                    Recompute now
                                </button>
                            </span>
                        )}
                    </div>

                    <DataTable
                        columns={columns}
                        data={filteredPairs}
                        pagination
                        paginationPerPage={25}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        striped
                        noDataComponent={
                            <div style={{ padding: 30, color: "var(--text-secondary)" }}>
                                {busy ? "Loading duplicates…" : "No duplicate clients found at this similarity threshold."}
                            </div>
                        }
                        customStyles={dataTableTheme}
                    />
                </>
            )}

            {view === "flags" && (
                <DataTable
                    columns={flagColumns}
                    data={flags}
                    pagination
                    highlightOnHover
                    striped
                    noDataComponent={
                        <div style={{ padding: 30, color: "var(--text-secondary)" }}>
                            No clients are currently flagged for deletion.
                        </div>
                    }
                    customStyles={dataTableTheme}
                />
            )}
        </div>
    );
};

const btnStyle = (variant: "primary" | "secondary" | "danger"): React.CSSProperties => {
    const base: React.CSSProperties = {
        padding: "5px 10px", fontSize: 12, fontWeight: 600,
        borderRadius: 4, border: "1px solid transparent", cursor: "pointer",
        display: "inline-flex", alignItems: "center",
    };
    if (variant === "primary") return { ...base, background: "#2ea043", color: "#fff" };
    if (variant === "danger") return { ...base, background: "#f8514922", color: "#f85149", borderColor: "#f8514955" };
    return { ...base, background: "var(--bg-primary)", color: "var(--text-primary)", borderColor: "var(--border-color, #30363d)" };
};

const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: "6px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: "pointer",
    border: "1px solid var(--border-color, #30363d)",
    background: active ? "var(--accent-color, #2ea043)" : "var(--bg-secondary)",
    color: active ? "#fff" : "var(--text-primary)",
});

const dataTableTheme = {
    table: { style: { background: "var(--bg-primary)", color: "var(--text-primary)" } },
    headRow: { style: { background: "var(--bg-secondary)", color: "var(--text-primary)", borderBottom: "1px solid var(--border-color, #30363d)" } },
    headCells: { style: { fontWeight: 700, fontSize: 12, textTransform: "uppercase" as const } },
    rows: {
        style: { background: "var(--bg-primary)", color: "var(--text-primary)", borderBottom: "1px solid var(--border-color, #30363d)" },
        stripedStyle: { background: "var(--bg-secondary)" },
        highlightOnHoverStyle: { background: "var(--bg-tertiary, var(--bg-secondary))", color: "var(--text-primary)", borderBottom: "1px solid var(--border-color, #30363d)" },
    },
    pagination: { style: { background: "var(--bg-secondary)", color: "var(--text-primary)", borderTop: "1px solid var(--border-color, #30363d)" } },
};

export default ClientDuplicates;
